import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { FloatingLayer } from '../component/floating'
import '../sso-workspace/sso-workspace.scss'
import './drawer.scss'

// The one slide-out panel (audit-trail spec §5): a right-side panel on
// desktop, a bottom sheet on phones (≤ 576 px). Escape, the Close button and
// a click on the backdrop close it. Extracted from the SSO workspace's
// .sw-drawer, whose colour tokens it keeps (the class stays on the root).
//
// Escape pressed inside the panel is handled there and stopped, so it does
// only that (a page's own document listeners, such as the claim chips', never
// see it). Escape pressed anywhere else reaches the document listener.
//
// back = { label, onClick } renders "← Back to {label}" (the panel was opened
// from somewhere else, such as an audit entry).
//
// phone = "full" fills a phone's screen instead of the bottom sheet (the
// email panel: a long timeline reads better full screen).
//
// subtitle: one muted line under the title (what the record is, an id).
// actions: the record's actions in the head, before Close, as the house
// header places them (owner 2026-10-30, the alert routing drawers): the shared
// Menu, its main action pinned as a button and the destructive one last in
// the ⋮ (like the SSO connection workspace's header menu). A ⋮ inside opens
// over the drawer (FloatingLayer).
export const DRAWER_FLOATING_LAYER = 1065
export const Drawer = ({ open, title, subtitle, label, onClose, back, actions, size = 'md', phone = 'sheet', children }) => {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return undefined
    const onKey = e => {
      if (e.key !== 'Escape') return
      if (onCloseRef.current) onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  const handleKeyDown = e => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    if (onClose) onClose()
  }

  return createPortal(
    <div className="edxr-drawer sw-drawer" onKeyDown={handleKeyDown} onClick={e => { if (e.target === e.currentTarget && onClose) onClose() }}>
      <div className={`edxr-drawer-panel sw-drawer-panel edxr-drawer-${size}${phone === 'full' ? ' edxr-drawer-phone-full' : ''}`} role="dialog" aria-modal="true" aria-label={label || title}>
        <div className="edxr-drawer-head">
          {back && <button type="button" className="btn btn-link p-0 edxr-drawer-back" onClick={back.onClick}>{`← Back to ${back.label}`}</button>}
          <div className="edxr-drawer-title flex-fill">
            <h2 className="h5 m-0">{title}</h2>
            {subtitle && <div className="edxr-drawer-sub">{subtitle}</div>}
          </div>
          {actions && <div className="edxr-drawer-actions" data-testid="drawer-actions"><FloatingLayer.Provider value={DRAWER_FLOATING_LAYER}>{actions}</FloatingLayer.Provider></div>}
          {onClose && <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />}
        </div>
        <FloatingLayer.Provider value={DRAWER_FLOATING_LAYER}>{children}</FloatingLayer.Provider>
      </div>
    </div>,
    document.body
  )
}
